import { Option } from "@inrev/core";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import { ClickableIcon, type ClickableIconProps, Icon, type IconType, Text } from "../primitives";
import { trackingAttrs } from "../utils";

const MIN_WIDTH = 300;
const DEFAULT_WIDTH = 360;

export const dockedPanelScrollRegionProps = { "data-panel-scroll": "" } as const;

export type DockedPanelActionProps = Omit<
	ClickableIconProps,
	"type" | "size" | "color" | "tooltip" | "aria-label" | "trackingId"
> & {
	icon: IconType;
	/** What screen readers announce, and the tooltip unless `tooltip` overrides it. */
	label: string;
	tooltip?: string;
	trackingId: string;
	/** Makes the action a toggle: announced as on or off, and tinted while on. */
	pressed?: boolean;
};

/** An icon-only control in the panel header. */
export const DockedPanelAction = ({
	icon,
	label,
	tooltip = label,
	pressed,
	className,
	...rest
}: DockedPanelActionProps) => (
	<ClickableIcon
		aria-label={label}
		aria-pressed={pressed}
		className={cn("rounded-md p-[3px]", pressed && "bg-surface-accent text-link", className)}
		color={pressed ? undefined : "secondary"}
		size="mini"
		tooltip={tooltip}
		type={icon}
		{...rest}
	/>
);

export type DockedPanelProps = {
	label: string;
	/** Element beside the label, naming what the panel holds. Defaults to a chat-bubble glyph. */
	leadingIcon?: ReactNode;
	/** `DockedPanelAction`s shown before Minimize while the panel is open. Clicks never toggle it. */
	actions?: ReactNode;
	children: ReactNode;
	contained?: boolean;
	deferChildrenUntilOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	testId?: string;
	trackingId?: string;
};

export const DockedPanel = ({
	label,
	leadingIcon,
	actions,
	children,
	contained,
	deferChildrenUntilOpen,
	onOpenChange,
	testId = "docked-panel",
	trackingId,
}: DockedPanelProps) => {
	const [open, setOpen] = useState(false);
	const [hasOpened, setHasOpened] = useState(false);
	const [panelWidth, setPanelWidth] = useState(DEFAULT_WIDTH);
	const [isResizing, setIsResizing] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);
	const cleanupResizeRef = useRef<(() => void) | null>(null);

	const clampWidth = useCallback((width: number) => {
		const maxWidth = window.innerWidth * 0.8;
		return Math.min(Math.max(width, MIN_WIDTH), maxWidth);
	}, []);

	const handleResizeStart = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			const startX = e.clientX;
			const startWidth = panelWidth;
			setIsResizing(true);

			const handleMouseMove = (e: MouseEvent) => {
				const newWidth = clampWidth(startWidth + (startX - e.clientX));
				setPanelWidth(newWidth);
			};

			const cleanup = () => {
				setIsResizing(false);
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", cleanup);
				cleanupResizeRef.current = null;
			};

			cleanupResizeRef.current = cleanup;
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", cleanup);
		},
		[panelWidth, clampWidth],
	);

	useEffect(() => {
		return () => cleanupResizeRef.current?.();
	}, []);

	// AccountLayout attaches a non-passive wheel listener on the page chrome that otherwise
	// hijacks gestures over this panel into the account scroll container. React's onWheel is
	// passive, so stopPropagation/preventDefault have to live on a native listener.
	useEffect(() => {
		if (!open) return;
		const panel = panelRef.current;
		if (panel === null) return;
		const onWheel = (e: WheelEvent) => {
			e.stopPropagation();
			const target = e.target instanceof Element ? e.target : null;
			if (target === null) {
				e.preventDefault();
				return;
			}
			const region = target.closest<HTMLElement>("[data-panel-scroll]");
			if (region === null || !panel.contains(region)) {
				e.preventDefault();
				return;
			}
			const atTop = region.scrollTop <= 0;
			const atBottom = Math.ceil(region.scrollTop + region.clientHeight) >= region.scrollHeight;
			if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) e.preventDefault();
		};
		panel.addEventListener("wheel", onWheel, { passive: false });
		return () => panel.removeEventListener("wheel", onWheel);
	}, [open]);

	const toggleOpen = () => {
		const next = !open;
		if (open) setPanelWidth(DEFAULT_WIDTH);
		if (next) setHasOpened(true);
		setOpen(next);
		onOpenChange?.(next);
	};

	const showChildren = deferChildrenUntilOpen ? hasOpened : true;

	return (
		<div
			className={cn(
				"z-[10000] w-[288px] shadow-md shadow-gray-400/50 rounded-t-[5px] bottom-0 right-[16px] bg-surface-base border border-b-0 border-bc-strong/80 overscroll-contain",
				contained ? "absolute" : "fixed",
				!isResizing && "transition-all ease duration-100",
				open
					? "h-[90%] min-h-[330px] sm:h-[90%] md:h-[80%]"
					: "h-[36px] shadow-md shadow-gray-400/40 border-bc-strong/80",
			)}
			data-docked-panel-open={open ? "" : undefined}
			data-testid={testId}
			ref={panelRef}
			style={open ? { width: panelWidth } : undefined}
		>
			{open && (
				<div
					className="absolute left-0 top-0 bottom-0 w-[5px] cursor-ew-resize z-10 hover:bg-bc-focus/30"
					data-testid={`${testId}-resize`}
					onMouseDown={handleResizeStart}
				/>
			)}
			<div className="flex flex-col w-full h-full overflow-hidden">
				<div className="flex h-[36px] shrink-0 items-center rounded-t-[5px] bg-surface-base">
					<button
						aria-expanded={open}
						className={cn(
							"flex h-full min-w-0 flex-1 items-center justify-between pl-[12px] text-left hover:bg-surface-light/50 rounded-t-[5px] cursor-pointer select-none focus-visible:outline-none focus-visible:ring focus-visible:ring-inset focus-visible:ring-brand-accent focus-visible:ring-opacity-30",
							open ? "pr-2" : "pr-[16px]",
						)}
						data-testid={`${testId}-toggle`}
						onClick={toggleOpen}
						type="button"
						{...trackingAttrs(trackingId)}
					>
						<div className="flex min-w-0 items-center space-x-[8px]">
							{Option.getOrElse(
								leadingIcon,
								<Icon type="chat-bubble-oval-left" size="mini" color="tertiary" />,
							)}
							<Text color="secondary" truncate type="body-strong">
								{label}
							</Text>
						</div>
						{!open && (
							<Icon type="chevron-up" size="mini" color="tertiary" className="stroke-[.7]" />
						)}
					</button>
					{open && (
						<div
							className="flex shrink-0 items-center gap-0.5 pr-[9px]"
							data-testid={`${testId}-actions`}
						>
							{actions}
							<DockedPanelAction
								icon="chevron-down"
								label="Minimize"
								onClick={toggleOpen}
								testId={`${testId}-minimize`}
								trackingId={`${Option.getOrElse(trackingId, testId)}-minimize`}
							/>
						</div>
					)}
				</div>
				<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
					{showChildren ? children : null}
				</div>
			</div>
		</div>
	);
};
